<script setup lang="ts">
import { DdtToastHost } from '@ddt/ui';
import { computed, watch } from 'vue';
import { useNav } from './navigation';
import RoomInviteToast from './ui/RoomInviteToast.vue';
import ScreenHost from './ui/ScreenHost.vue';
import SoundMixer from './components/SoundMixer.vue';
import { muted, mixerOpen, toggleMute } from './audio';
import { useAudioDirector } from './audioDirector';
import { useCatalog } from './stores/catalog';
import { useFriends } from './stores/friends';
import { useMatch } from './stores/match';
import { useSession } from './stores/session';
import { preloadVersus } from './versusPreload';

const session = useSession();
const match = useMatch();
const nav = useNav();
useAudioDirector();

// Só em desenvolvimento: dá aos testes de ponta a ponta e ao console acesso ao estado da partida.
if (import.meta.env.DEV) (window as unknown as { __ddt: unknown }).__ddt = { match, session, nav };

// A conexão do Match vive enquanto houver sessão: lobby, sala e partida compartilham o mesmo WebSocket.
watch(
  () => session.account?.id,
  (id) => {
    if (id) match.connect(id);
    else {
      match.disconnect();
      useFriends().reset();
    }
  },
  { immediate: true },
);

/** Teto da espera pelo aquecimento do confronto: com rede lenta a tela abre mesmo assim e baixa o que faltar. */
const VERSUS_PRELOAD_CAP_MS = 3000;
let startToken = 0;

// Partida começou (por convite, fila ou reconexão): vai para a tela de jogo.
// Na sala de PvP vai para a tela de confronto (times e mapa), que abre a partida. Antes, a sala aquece o confronto (catálogo, bonecos, armas e artes)
// por até `VERSUS_PRELOAD_CAP_MS`, para os dois times aparecerem prontos. Em outro lugar (reconexão, por exemplo) vai direto para a partida.
watch(
  () => match.phase,
  async (phase) => {
    const token = ++startToken;
    const here = nav.current.value.name;
    if (phase !== 'playing' || here === 'match' || here === 'versus') return;
    if (here !== 'pvp') {
      nav.push('match');
      return;
    }
    const players = (match.room?.members ?? []).map((m) => ({ accountId: m.accountId, sex: m.sex, style: m.style, weaponTemplateId: m.weaponTemplateId, weaponStrengthenLevel: m.weaponStrengthenLevel }));
    await Promise.race([
      preloadVersus(players, useCatalog(), `${import.meta.env.BASE_URL}collision/craters`),
      new Promise((resolve) => setTimeout(resolve, VERSUS_PRELOAD_CAP_MS)),
    ]);
    if (token === startToken && match.phase === 'playing' && nav.current.value.name === 'pvp') nav.push('versus');
  },
);

const nick = computed(() => session.character?.nickName ?? session.account?.username ?? '');

async function logout() {
  await session.logout();
  nav.reset('home');
}
</script>

<template>
  <div class="game-root">
    <main id="main" class="page-host">
      <ScreenHost />
    </main>
    <div v-if="session.authenticated" class="game-user" :class="{ 'game-user--bottom': ['hall', 'inventory'].includes(nav.current.value.name) }" role="toolbar" aria-label="Conta">
      <span data-testid="nick">{{ nick }}</span>
      <button type="button" :aria-pressed="muted" :aria-label="muted ? 'Ligar o som' : 'Desligar o som'" data-testid="mute" @click="toggleMute">{{ muted ? '🔇' : '🔊' }}</button>
      <button type="button" aria-label="Abrir o mixer de som" data-testid="mixer-open" @click="mixerOpen = true">Mixer</button>
      <button type="button" @click="logout">Sair</button>
    </div>
  </div>
  <SoundMixer />
  <RoomInviteToast v-if="session.authenticated" />
  <DdtToastHost />
</template>

<style scoped>
.game-root {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background: radial-gradient(ellipse at 50% 30%, #17306b 0%, #0b1638 55%, #050a1c 100%);
}
.page-host {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.game-user {
  position: fixed;
  top: 6px;
  right: 8px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 14px;
  background: rgb(0 0 0 / 55%);
  color: #fff;
  font: 700 12px Arial, sans-serif;
  opacity: 0.55;
  transition: opacity 0.15s;
}
/* No Salão das Fendas o canto superior direito é das moedas. */
.game-user--bottom {
  top: auto;
  bottom: 6px;
}
.game-user:hover,
.game-user:focus-within {
  opacity: 1;
}
.game-user button {
  padding: 2px 8px;
  font: 700 12px Arial, sans-serif;
  color: #fff;
  cursor: pointer;
  border: 1px solid #ffffff55;
  border-radius: 10px;
  background: transparent;
}
</style>
